import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from './ToastContext';
import { buildCartLineIdFromOptions, cartLinesMatch } from '../Utils/cartItem';
import {
    buildCartItemFromWishlist,
    getPrimaryIdentifier,
    identifiersMatch,
    normalizeWishlistProduct,
} from '../Utils/wishlistItem';
import { getProductImageUrl } from '../Utils/productImage';
import { addWishlistItem, getWishlist, removeWishlistItem } from '../Api/wishlistApi';

const CartContext = createContext();
const CART_EXPIRY_MS = 30 * 60 * 1000;
const WISHLIST_STORAGE_KEY = 'wishlist';
const WISHLIST_TTL_MS = 30 * 60 * 1000;
const WISHLIST_MERGE_FLAG_KEY = 'wishlistMergedForToken';

const readWishlistFromStorage = () => {
    try {
        const savedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
        if (!savedWishlist) return [];

        const parsed = JSON.parse(savedWishlist);

        if (Array.isArray(parsed)) return parsed;

        if (!parsed || !Array.isArray(parsed.items)) {
            localStorage.removeItem(WISHLIST_STORAGE_KEY);
            return [];
        }

        if (!parsed.expiresAt || Date.now() > parsed.expiresAt) {
            localStorage.removeItem(WISHLIST_STORAGE_KEY);
            return [];
        }

        return parsed.items;
    } catch {
        localStorage.removeItem(WISHLIST_STORAGE_KEY);
        return [];
    }
};

const writeWishlistToStorage = (items) => {
    localStorage.setItem(
        WISHLIST_STORAGE_KEY,
        JSON.stringify({
            items,
            expiresAt: Date.now() + WISHLIST_TTL_MS,
        })
    );
};

const readCartFromStorage = () => {
    try {
        const savedCart = localStorage.getItem('cart');
        const cartExpiry = localStorage.getItem('cartExpiry');
        if (!savedCart || !cartExpiry) return [];

        const expiryMs = parseInt(cartExpiry, 10);
        if (Number.isNaN(expiryMs) || Date.now() >= expiryMs) {
            localStorage.removeItem('cart');
            localStorage.removeItem('cartExpiry');
            return [];
        }

        const parsed = JSON.parse(savedCart);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        localStorage.removeItem('cart');
        localStorage.removeItem('cartExpiry');
        return [];
    }
};

const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const isObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || ''));

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const { addToast } = useToast();
    const { t } = useTranslation();
    const [authToken, setAuthToken] = useState(() => localStorage.getItem('token') || '');
    const [cartItems, setCartItems] = useState(() => readCartFromStorage());
    const [wishlistItems, setWishlistItems] = useState(() => readWishlistFromStorage());
    const [cartRemainingTime, setCartRemainingTime] = useState(0);
    const cartItemsRef = useRef(cartItems);
    cartItemsRef.current = cartItems;

    const normalizeWishlistItems = useCallback((wishlist = []) => {
        return wishlist
            .map((entry) => {
                const product = entry?.product || entry;
                return normalizeWishlistProduct(product);
            })
            .filter(Boolean);
    }, []);

    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cartItems));
    }, [cartItems]);

    useEffect(() => {
        if (cartItems.length > 0) {
            localStorage.setItem('cartExpiry', String(Date.now() + CART_EXPIRY_MS));
        } else {
            localStorage.removeItem('cartExpiry');
        }
    }, [cartItems]);

    useEffect(() => {
        writeWishlistToStorage(wishlistItems);
    }, [wishlistItems]);

    useEffect(() => {
        const syncTokenState = () => {
            const latestToken = localStorage.getItem('token') || '';
            setAuthToken((prev) => (prev === latestToken ? prev : latestToken));
        };

        window.addEventListener('focus', syncTokenState);
        window.addEventListener('storage', syncTokenState);

        return () => {
            window.removeEventListener('focus', syncTokenState);
            window.removeEventListener('storage', syncTokenState);
        };
    }, []);

    useEffect(() => {
        let isCancelled = false;

        const syncWishlist = async () => {
            if (!authToken) {
                setWishlistItems(readWishlistFromStorage());
                return;
            }

            try {
                let response = await getWishlist();
                let serverWishlist = normalizeWishlistItems(response?.data?.wishlist || []);

                const alreadyMergedToken = sessionStorage.getItem(WISHLIST_MERGE_FLAG_KEY);
                if (alreadyMergedToken !== authToken) {
                    const localWishlist = readWishlistFromStorage();
                    const serverIds = new Set(
                        serverWishlist
                            .map((item) => getPrimaryIdentifier(item))
                            .filter(Boolean)
                            .map(String)
                    );

                    const mergeCandidates = localWishlist.filter(
                        (item) => {
                            const id = getPrimaryIdentifier(item);
                            return id && isObjectId(id) && !serverIds.has(String(id));
                        }
                    );

                    if (mergeCandidates.length > 0) {
                        await Promise.allSettled(
                            mergeCandidates.map((item) => addWishlistItem(getPrimaryIdentifier(item)))
                        );
                        response = await getWishlist();
                        serverWishlist = normalizeWishlistItems(response?.data?.wishlist || []);
                    }

                    sessionStorage.setItem(WISHLIST_MERGE_FLAG_KEY, authToken);
                }

                if (!isCancelled) {
                    setWishlistItems(serverWishlist);
                }
            } catch (error) {
                if (error?.status === 401 && !isCancelled) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('userId');
                    setAuthToken('');
                    setWishlistItems(readWishlistFromStorage());
                    return;
                }

                console.error('Failed to sync wishlist from server:', error);
                if (!isCancelled) {
                    setWishlistItems(readWishlistFromStorage());
                }
            }
        };

        syncWishlist();

        return () => {
            isCancelled = true;
        };
    }, [authToken, normalizeWishlistItems]);

    useEffect(() => {
        const interval = setInterval(() => {
            const expiry = localStorage.getItem('cartExpiry');
            if (expiry) {
                const remaining = Math.max(0, parseInt(expiry, 10) - Date.now());
                setCartRemainingTime(remaining);
                if (remaining === 0) {
                    setCartItems([]);
                    localStorage.removeItem('cart');
                    localStorage.removeItem('cartExpiry');
                }
            } else {
                setCartRemainingTime(0);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const addToCart = useCallback((item) => {
        const lineId =
            item.cartLineId ||
            buildCartLineIdFromOptions(item.productId, item.selectedOptions) ||
            `${item.productId}::${item.size || ''}::${item.color || ''}`;
        const quantityToAdd = Math.max(1, Number(item.quantity) || 1);
        const prevCart = cartItemsRef.current;
        const existingItem = prevCart.find((i) => cartLinesMatch(i, lineId));

        addToast(
            existingItem ? t('toast.itemUpdated') : t('toast.addedToCart'),
            'success'
        );

        setCartItems((prevItems) => {
            const existing = prevItems.find((i) => cartLinesMatch(i, lineId));
            if (existing) {
                return prevItems.map((i) =>
                    cartLinesMatch(i, lineId)
                        ? { ...i, quantity: i.quantity + quantityToAdd }
                        : i
                );
            }
            return [...prevItems, { ...item, cartLineId: lineId, quantity: quantityToAdd }];
        });
    }, [addToast, t]);

    const removeFromCart = useCallback((lineId) => {
        setCartItems((prevItems) => prevItems.filter((item) => !cartLinesMatch(item, lineId)));
    }, []);

    const updateCartItemQuantity = useCallback((lineId, quantity) => {
        const nextQty = Number(quantity);
        if (nextQty < 1) return;
        setCartItems((prevItems) =>
            prevItems.map((item) =>
                cartLinesMatch(item, lineId) ? { ...item, quantity: nextQty } : item
            )
        );
    }, []);

    const clearCart = useCallback(() => {
        setCartItems([]);
    }, []);

    const addToWishlist = useCallback(async (item) => {
        const itemId = getPrimaryIdentifier(item);

        if (wishlistItems.some((existingItem) => identifiersMatch(existingItem, itemId))) {
            addToast(t('toast.alreadyInWishlist'), 'info');
            return;
        }

        const normalizedItem = {
            ...item,
            imageUrl: getProductImageUrl(item) || item.imageUrl,
        };

        if (authToken && item?._id) {
            try {
                const response = await addWishlistItem(item._id);
                setWishlistItems(normalizeWishlistItems(response?.data?.wishlist || []));
                addToast(t('toast.addedToWishlist'), 'success');
                return;
            } catch (error) {
                addToast(error?.message || t('wishlist.updateFailed'), 'error');
                return;
            }
        }

        addToast(t('toast.addedToWishlist'), 'success');
        setWishlistItems((prevItems) => {
            if (prevItems.some((existingItem) => identifiersMatch(existingItem, itemId))) {
                return prevItems;
            }
            return [...prevItems, normalizedItem];
        });
    }, [addToast, authToken, normalizeWishlistItems, t, wishlistItems]);

    const removeFromWishlist = useCallback(async (itemId) => {
        if (authToken) {
            const currentItem = wishlistItems.find((item) => identifiersMatch(item, itemId));
            const backendId = getPrimaryIdentifier(currentItem) || (isObjectId(itemId) ? itemId : null);

            if (!backendId) {
                addToast(t('toast.removedFromWishlist'), 'success');
                setWishlistItems((prevItems) => prevItems.filter((item) => !identifiersMatch(item, itemId)));
                return;
            }

            try {
                const response = await removeWishlistItem(backendId);
                setWishlistItems(normalizeWishlistItems(response?.data?.wishlist || []));
                addToast(t('toast.removedFromWishlist'), 'success');
                return;
            } catch (error) {
                addToast(error?.message || t('wishlist.updateFailed'), 'error');
                return;
            }
        }

        addToast(t('toast.removedFromWishlist'), 'success');
        setWishlistItems((prevItems) => prevItems.filter((item) => !identifiersMatch(item, itemId)));
    }, [addToast, authToken, normalizeWishlistItems, t, wishlistItems]);

    const isInWishlist = useCallback((itemId) => {
        return wishlistItems.some((item) => identifiersMatch(item, itemId));
    }, [wishlistItems]);

    const moveToCart = useCallback(async (itemId) => {
        const item = wishlistItems.find((existingItem) => identifiersMatch(existingItem, itemId));
        if (item) {
            addToCart(buildCartItemFromWishlist(item));
            await removeFromWishlist(itemId);
        }
    }, [addToCart, removeFromWishlist, wishlistItems]);

    const getCartTotal = useCallback(() => {
        return cartItems.reduce((total, item) => total + Number(item.price) * Number(item.quantity), 0);
    }, [cartItems]);

    const getCartItemsCount = useCallback(() => {
        return cartItems.reduce((count, item) => count + Number(item.quantity), 0);
    }, [cartItems]);

    const value = {
        cartItems,
        wishlistItems,
        cartRemainingTime,
        formatTime,
        addToCart,
        removeFromCart,
        updateCartItemQuantity,
        clearCart,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        moveToCart,
        getCartTotal,
        getCartItemsCount,
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;

